/**
 * Single source of truth for dashboard sidebar: 4 chapters + Support + Discover.
 * Demo URLs use /demo/{demoSlug}/{segment}; Support (Community + Connect) uses one
 * canonical demo slug for all chapters — see DEMO_SUPPORT_DEMO_SLUG.
 */

export const CHAPTER_NAV = {
  ttc: {
    emoji: '💜',
    label: 'TTC',
    color: '#7F77DD',
    colorLight: '#EEEDFE',
    /** Active nav item text — matches --purple-dk */
    navActiveText: 'var(--purple-dk)',
    demoSlug: 'trying-to-conceive',
    /** TTC uses only these sections — no shared SUPPORT/DISCOVER block (see DashboardNav). */
    sections: [
      {
        heading: 'MY JOURNEY',
        items: [
          { icon: '🏠', label: 'Home', segment: 'home' },
          { icon: '📅', label: 'My Cycle', segment: 'cycle' },
          { icon: '📋', label: 'Health Record', segment: 'health' },
          { icon: '🔬', label: 'Cycle Intelligence', segment: 'cycle-intelligence' },
        ],
      },
      {
        heading: 'LEARN',
        items: [{ icon: '📖', label: 'Cycle School', segment: 'cycle-school' }],
      },
      {
        heading: 'DECIDE',
        items: [
          { icon: '👩‍⚕️', label: 'My Doctors', segment: 'doctors' },
          { icon: '📋', label: 'Appointment Prep', segment: 'appointment-prep' },
          { icon: '🧭', label: 'What to Expect', segment: 'when-to-see-a-doctor' },
        ],
      },
      {
        heading: 'SUPPORT',
        items: [
          { icon: '👥', label: 'Community', segment: 'community' },
          { icon: '💕', label: 'Connect', segment: 'connect' },
          { icon: '🤝', label: 'Partner Support', segment: 'partner-support' },
          { icon: '🕊️', label: 'Loss & Recovery', segment: 'loss-and-recovery' },
        ],
      },
      {
        heading: 'DISCOVER',
        items: [
          { icon: '📚', label: 'Resources', segment: 'resources' },
          { icon: '🛍️', label: 'Products', segment: 'products' },
          { icon: '📅', label: 'Events Near Me', segment: 'events' },
        ],
      },
    ],
  },
  fertility: {
    emoji: '🌸',
    label: 'Fertility',
    color: '#D4537E',
    colorLight: '#FBEAF0',
    demoSlug: 'fertility',
    /** Fertility includes its own SUPPORT/DISCOVER — see DashboardNav (no SHARED_NAV duplicate). */
    sections: [
      {
        heading: 'MY JOURNEY',
        items: [
          { icon: '🏠', label: 'Home', segment: 'home' },
          { icon: '🧭', label: 'What to Expect', segment: 'when-to-see-a-doctor' },
        ],
      },
      {
        heading: 'TRACK',
        items: [
          { icon: '🗺️', label: 'My Journey', segment: 'myjourney' },
          { icon: '📋', label: 'Health Record', segment: 'health' },
          { icon: '💰', label: 'Financial Tracker', segment: 'financial' },
          { icon: '💊', label: 'Medications', segment: 'medications' },
        ],
      },
      {
        heading: 'DECIDE',
        items: [
          { icon: '👩‍⚕️', label: 'My Doctors', segment: 'doctors' },
          { icon: '📋', label: 'Appointment Prep', segment: 'appointment-prep' },
        ],
      },
      {
        heading: 'SUPPORT',
        items: [
          { icon: '👥', label: 'Community', segment: 'community' },
          { icon: '💕', label: 'Connect', segment: 'connect' },
          { icon: '🌈', label: 'LGBTQ+', segment: 'lgbtq' },
          { icon: '🤝', label: 'Partner Support', segment: 'partner-support' },
          { icon: '🕊️', label: 'Loss & Recovery', segment: 'loss' },
        ],
      },
      {
        heading: 'DISCOVER',
        items: [
          { icon: '📚', label: 'Resources', segment: 'resources' },
          { icon: '📖', label: 'Guides', segment: 'guides' },
          { icon: '🛍️', label: 'Products', segment: 'products' },
          { icon: '📅', label: 'Events Near Me', segment: 'events' },
        ],
      },
    ],
  },
  pregnancy: {
    emoji: '🤰',
    label: 'Pregnancy',
    color: '#0F766E',
    colorLight: '#F0FDFA',
    demoSlug: 'pregnancy',
    sections: [
      {
        heading: 'MY JOURNEY',
        items: [
          { icon: '🏠', label: 'Home', segment: 'home' },
          { icon: '📆', label: 'Week by Week', segment: 'myjourney' },
          { icon: '📋', label: 'Health Record', segment: 'health' },
          { icon: '🩺', label: 'Appointments', segment: 'doctor' },
          { icon: '👩‍⚕️', label: 'My Doctors', segment: 'doctors' },
          { icon: '📝', label: 'Birth Plan', segment: 'birthplan' },
          { icon: '🕊️', label: 'Loss & Recovery', segment: 'loss' },
        ],
      },
    ],
  },
  motherhood: {
    emoji: '🤱',
    label: 'Motherhood',
    color: '#9B2942',
    colorLight: '#F5EAED',
    demoSlug: 'postpartum',
    sections: [
      {
        heading: 'MY JOURNEY',
        items: [
          { icon: '🏠', label: 'Home', segment: 'home' },
          { icon: '👶', label: 'My Baby', segment: 'baby' },
          { icon: '😴', label: 'Sleep Tracker', segment: 'sleep' },
          { icon: '🍼', label: 'Feeding Log', segment: 'feeding' },
          { icon: '📏', label: 'Milestones', segment: 'milestones' },
          { icon: '💗', label: 'My Wellbeing', segment: 'wellbeing' },
          { icon: '🕊️', label: 'Loss & Recovery', segment: 'loss' },
        ],
      },
    ],
  },
};

/** Single demo “chapter” slug for shared Support destinations (Community + Connect). */
export const DEMO_SUPPORT_DEMO_SLUG = 'fertility';

export const SHARED_NAV = [
  {
    heading: 'SUPPORT',
    items: [
      { icon: '👥', label: 'Community', segment: 'community' },
      { icon: '💕', label: 'Connect', segment: 'connect' },
    ],
  },
  {
    heading: 'DISCOVER',
    items: [
      { icon: '📚', label: 'Resources', segment: 'resources' },
      { icon: '🛍️', label: 'Products', segment: 'products' },
      { icon: '📅', label: 'Events Near Me', segment: 'events' },
    ],
  },
];

const CHAPTER_KEYS = ['fertility', 'ttc', 'pregnancy', 'motherhood'];

/** Shown in the demo chapter switcher but not navigable until launch. */
const DEMO_CHAPTER_COMING_SOON_KEYS = new Set(['ttc', 'pregnancy', 'motherhood']);

export function isDemoChapterComingSoon(key) {
  return DEMO_CHAPTER_COMING_SOON_KEYS.has(key);
}

/** Map demo URL segment (e.g. postpartum) → chapterNav key. */
export function demoSlugToChapterKey(slug) {
  if (!slug) return 'fertility';
  const s = String(slug).toLowerCase();
  if (s === 'cycle' || s === 'first-chapter' || s === 'trying-to-conceive') return 'ttc';
  if (s === 'postpartum' || s === 'motherhood' || s === 'menopause') return 'motherhood';
  if (s === 'ttc' || s === 'fertility' || s === 'pregnancy') return s;
  return 'fertility';
}

/** Map URL path → chapterNav key (4 chapters). */
export function getChapterKeyFromPath(pathname) {
  const m = String(pathname || '').match(/^\/demo\/([^/]+)/);
  if (m) return demoSlugToChapterKey(m[1]);
  return null;
}

/** Infer chapter from user journey when not on a demo URL. */
export function getChapterKeyFromJourneyType(journeyType) {
  const j = String(journeyType || 'ivf').toLowerCase();
  if (['ttc', 'cycle'].includes(j)) return 'ttc';
  if (['fertility', 'ivf', 'loss', 'surrogacy'].includes(j)) return 'fertility';
  if (j === 'pregnancy') return 'pregnancy';
  if (['postpartum', 'baby', 'motherhood', 'menopause'].includes(j)) return 'motherhood';
  return 'fertility';
}

export function getDefaultChapterKey(pathname, journeyType) {
  return getChapterKeyFromPath(pathname) || getChapterKeyFromJourneyType(journeyType);
}

/** Build /demo/{slug}/segment */
export function demoHref(demoSlug, segment) {
  return `/demo/${demoSlug}/${segment}`;
}

/** Unified demo URLs for Community + Connect (same pages for every chapter). */
export function demoSupportHref(segment) {
  return demoHref(DEMO_SUPPORT_DEMO_SLUG, segment);
}

const SEGMENT_APP_PATH = {
  home: '/',
  myjourney: '/journey',
  opk: '/journey',
  tww: '/journey',
  health: '/health',
  doctor: '/doctors',
  doctors: '/doctors',
  financial: '/financial',
  medications: '/medications',
  birthplan: '/journey',
  baby: '/journey',
  sleep: '/journey',
  feeding: '/journey',
  milestones: '/journey',
  wellbeing: '/journey',
  cycle: '/journey',
  'cycle-intelligence': '/journey',
  'cycle-school': '/journey',
  'appointment-prep': '/journey',
  'when-to-see-a-doctor': '/journey',
  'partner-support': '/community',
  lgbtq: '/community',
  'loss-and-recovery': '/community',
  community: '/community',
  connect: '/connect',
  loss: '/community',
  events: '/events',
  resources: '/resources',
  guides: '/resources',
  products: '/products',
};

export function appPathForSegment(segment) {
  return SEGMENT_APP_PATH[segment] || '/';
}

export { CHAPTER_KEYS };
